if (!window.SatellaiZoomiesEventLoaded) {
  window.SatellaiZoomiesEventLoaded = true;

  class SatellaiZoomiesEvent {
    constructor(root) {
      this.root = root;
      this.cards = Array.from(root.querySelectorAll('[data-zoomies-product-card]'));
      this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';
      this.klarnaRefreshTimer = null;
      this.init();
    }

    init() {
      this.cards.forEach((card) => {
        this.bindProductGallery(card);
        this.bindVariantSelect(card);
        this.bindQuantityControls(card);
        this.bindAddToCart(card);
      });
      this.refreshKlarnaPlacements();
    }

    bindProductGallery(card) {
      const image = card.querySelector('[data-product-image]');
      const thumbs = Array.from(card.querySelectorAll('[data-product-gallery-thumb]'));

      if (!image || thumbs.length === 0) return;

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const nextImage = thumb.dataset.image;
          if (!nextImage) return;

          image.src = nextImage;
          image.alt = thumb.dataset.imageAlt || image.alt;

          thumbs.forEach((item) => {
            const isActive = item === thumb;
            item.classList.toggle('is-active', isActive);
            if (isActive) {
              item.setAttribute('aria-current', 'true');
            } else {
              item.removeAttribute('aria-current');
            }
          });
        });
      });
    }

    normalizeImageUrl(url) {
      if (!url) return '';

      const parser = document.createElement('a');
      parser.href = url;
      return parser.href.replace(/^https?:/, '');
    }

    normalizeImageToken(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/&quot;|&#34;/g, ' ')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    }

    getOptionImageTokens(option) {
      if (!option) return [];

      const stopwords = new Set([
        'free',
        'month',
        'months',
        'plan',
        'year',
        'years',
        'pack',
        'membership',
        'included'
      ]);
      const values = [
        option.dataset.option0,
        option.dataset.option1,
        option.dataset.option2,
        option.dataset.optionLabel,
        option.textContent
      ];

      return Array.from(new Set(values
        .flatMap((value) => this.normalizeImageToken(value).split(' '))
        .filter((token) => token.length >= 3 && !/^\d+$/.test(token) && !stopwords.has(token))));
    }

    findGalleryThumbForOption(card, option) {
      const thumbs = Array.from(card.querySelectorAll('[data-product-gallery-thumb]'));
      if (thumbs.length === 0) return null;

      const tokens = this.getOptionImageTokens(option);
      if (tokens.length === 0) return null;

      let bestThumb = null;
      let bestScore = 0;

      thumbs.forEach((thumb) => {
        const haystack = this.normalizeImageToken(`${thumb.dataset.image || ''} ${thumb.dataset.imageAlt || ''}`);
        const score = tokens.reduce((total, token) => total + (haystack.includes(token) ? 1 : 0), 0);

        if (score > bestScore) {
          bestScore = score;
          bestThumb = thumb;
        }
      });

      return bestThumb;
    }

    syncProductImage(card, option) {
      let nextImage = option ? option.dataset.variantImage : '';
      const image = card.querySelector('[data-product-image]');
      if (!image) return;
      if (image.dataset.productImageLocked === 'true') return;

      const thumbs = Array.from(card.querySelectorAll('[data-product-gallery-thumb]'));
      let activeThumb = null;

      if (!nextImage) {
        activeThumb = this.findGalleryThumbForOption(card, option);
        nextImage = activeThumb ? activeThumb.dataset.image : '';
      }

      if (!nextImage) return;

      image.src = nextImage;
      image.alt = option.dataset.variantImageAlt || (activeThumb ? activeThumb.dataset.imageAlt : '') || option.dataset.optionLabel || image.alt;

      const normalizedImage = this.normalizeImageUrl(nextImage);
      if (thumbs.length === 0) return;

      activeThumb = activeThumb || thumbs.find((thumb) => this.normalizeImageUrl(thumb.dataset.image) === normalizedImage);

      thumbs.forEach((thumb) => {
        const isActive = thumb === activeThumb;
        thumb.classList.toggle('is-active', isActive);
        if (isActive) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    }

    bindVariantSelect(card) {
      const select = card.querySelector('[data-variant-select]');
      if (!select) return;

      const optionControls = Array.from(card.querySelectorAll('[data-product-option-control]'));
      const variantOptions = Array.from(select.options);
      const planKeyword = this.normalizePlanKeyword(select.dataset.planKeyword);
      const getOptionValue = (option, index) => option ? option.getAttribute(`data-option-${index}`) || '' : '';
      const getControlIndex = (control) => parseInt(control.dataset.optionIndex, 10);
      const getControlValue = (control) => control.dataset.optionValue || control.value || '';

      const optionMatches = (option, selectedValues) => selectedValues.every((value, index) => {
        if (!value) return true;
        return getOptionValue(option, index) === value;
      });

      const optionMatchesPlan = (option) => {
        if (!planKeyword) return true;

        return [
          option.dataset.option0,
          option.dataset.option1,
          option.dataset.option2,
          option.dataset.optionLabel,
          option.textContent
        ].some((value) => this.normalizePlanKeyword(value).includes(planKeyword));
      };

      optionControls.forEach((optionControl) => {
        const eventName = optionControl.matches('select') ? 'change' : 'click';
        optionControl.addEventListener(eventName, () => {
          const currentOption = select.options[select.selectedIndex];
          const selectedValues = [0, 1, 2].map((index) => getOptionValue(currentOption, index));
          const changedIndex = getControlIndex(optionControl);

          optionControls.forEach((visibleControl) => {
            const index = getControlIndex(visibleControl);
            if (!Number.isFinite(index)) return;

            if (visibleControl === optionControl || visibleControl.matches('select') || visibleControl.getAttribute('aria-pressed') === 'true') {
              selectedValues[index] = getControlValue(visibleControl);
            }
          });

          if (Number.isFinite(changedIndex)) {
            selectedValues[changedIndex] = getControlValue(optionControl);
          }

          const nextOption = variantOptions.find((option) => !option.disabled && optionMatches(option, selectedValues) && optionMatchesPlan(option))
            || variantOptions.find((option) => !option.disabled && optionMatches(option, selectedValues))
            || variantOptions.find((option) => {
              if (option.disabled) return false;
              if (!optionMatchesPlan(option)) return false;
              return optionControls.every((visibleControl) => {
                const index = getControlIndex(visibleControl);
                if (!Number.isFinite(index)) return true;
                if (visibleControl.hasAttribute('data-product-option-button') && index === changedIndex && visibleControl !== optionControl) return true;
                if (visibleControl.hasAttribute('data-product-option-button') && visibleControl.getAttribute('aria-pressed') !== 'true' && visibleControl !== optionControl) return true;
                return getOptionValue(option, index) === getControlValue(visibleControl);
              });
            })
            || variantOptions.find((option) => {
              if (option.disabled) return false;
              return optionControls.every((visibleControl) => {
                const index = getControlIndex(visibleControl);
                if (!Number.isFinite(index)) return true;
                if (visibleControl.hasAttribute('data-product-option-button') && index === changedIndex && visibleControl !== optionControl) return true;
                if (visibleControl.hasAttribute('data-product-option-button') && visibleControl.getAttribute('aria-pressed') !== 'true' && visibleControl !== optionControl) return true;
                return getOptionValue(option, index) === getControlValue(visibleControl);
              });
            });

          if (nextOption) {
            select.value = nextOption.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });

      const updateFromSelect = (shouldSyncImage = true) => {
        const option = select.options[select.selectedIndex];
        if (!option) return;

        const price = card.querySelector('[data-product-price]');
        const comparePrice = card.querySelector('[data-product-compare-price]');
        const optionLabel = card.querySelector('[data-product-option-label]');
        const quantityInput = card.querySelector('[data-quantity-input]');
        const addButton = card.querySelector('[data-add-to-cart]');
        const klarnaPlacement = card.querySelector('[data-klarna-placement]');
        const badge = card.querySelector('[data-product-badge]');
        const quantity = this.normalizeQuantity(quantityInput ? quantityInput.value : 1);
        const priceCents = parseInt(option.dataset.priceCents, 10);
        const comparePriceCents = parseInt(option.dataset.comparePriceCents, 10);
        const visibleLabels = [];
        const syncedIndexes = new Set();

        // 同步隐藏 variant 与前台可见的颜色/尺寸选项，保留隐藏的订阅套餐选项。
        optionControls.forEach((optionControl) => {
          const index = getControlIndex(optionControl);
          if (!Number.isFinite(index)) return;

          const value = getOptionValue(option, index);
          if (optionControl.matches('select')) {
            optionControl.value = value;
          } else {
            const isActive = value === getControlValue(optionControl);
            optionControl.classList.toggle('is-active', isActive);
            optionControl.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          }

          if (value) {
            if (!syncedIndexes.has(index)) {
              visibleLabels.push(value);
              syncedIndexes.add(index);
            }
          }
        });

        if (optionLabel) {
          optionLabel.textContent = visibleLabels.length > 0
            ? visibleLabels.join(' / ')
            : option.dataset.optionLabel || option.textContent.trim();
        }

        if (shouldSyncImage) {
          this.syncProductImage(card, option);
        }

        if (addButton) {
          addButton.disabled = option.disabled || !option.value;
        }

        this.syncProductMediaLink(card, option.value);
        this.syncKlarnaPlacement(klarnaPlacement, priceCents);
        this.syncSaveBadge(badge, priceCents, comparePriceCents, quantity);

        if (price) {
          price.textContent = Number.isFinite(priceCents)
            ? this.formatMoney(priceCents * quantity)
            : option.dataset.price || '';
        }

        if (comparePrice) {
          comparePrice.textContent = Number.isFinite(comparePriceCents)
            ? this.formatMoney(comparePriceCents * quantity)
            : option.dataset.comparePrice || '';
          comparePrice.hidden = !option.dataset.comparePrice;
        }
      };

      select.addEventListener('change', (event) => {
        updateFromSelect(!event.detail?.skipVariantImage);
      });
      updateFromSelect(false);
    }

    normalizeQuantity(value) {
      const parsed = parseInt(value, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    }

    normalizePlanKeyword(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '');
    }

    syncKlarnaPlacement(placement, priceCents) {
      if (!placement || !Number.isFinite(priceCents)) return;

      const nextAmount = String(priceCents);
      if (placement.getAttribute('data-purchase-amount') === nextAmount) return;

      placement.setAttribute('data-purchase-amount', nextAmount);
      this.refreshKlarnaPlacements();
    }

    syncSaveBadge(badge, priceCents, comparePriceCents, quantity) {
      if (!badge) return;

      if (!Number.isFinite(priceCents) || !Number.isFinite(comparePriceCents) || comparePriceCents <= priceCents) {
        badge.textContent = '';
        badge.hidden = true;
        return;
      }

      badge.textContent = `Save ${this.formatMoney((comparePriceCents - priceCents) * quantity)}`;
      badge.hidden = false;
    }

    buildVariantUrl(baseUrl, variantId) {
      if (!baseUrl || !variantId) return baseUrl || '';

      try {
        const url = new URL(baseUrl, window.location.origin);
        url.searchParams.set('variant', variantId);
        return url.origin === window.location.origin
          ? `${url.pathname}${url.search}${url.hash}`
          : url.href;
      } catch (error) {
        return baseUrl.includes('?')
          ? `${baseUrl}&variant=${variantId}`
          : `${baseUrl}?variant=${variantId}`;
      }
    }

    syncProductMediaLink(card, variantId) {
      const link = card.querySelector('[data-product-media-link]');
      if (!link) return;

      const baseUrl = link.dataset.productUrl || link.getAttribute('href') || '';
      link.href = this.buildVariantUrl(baseUrl, variantId);
    }

    refreshKlarnaPlacements() {
      if (!this.root.querySelector('[data-klarna-placement]')) return;

      window.clearTimeout(this.klarnaRefreshTimer);
      this.klarnaRefreshTimer = window.setTimeout(() => {
        // Klarna OSM 在不同安装方式下暴露的刷新入口不同，这里兼容官方新旧接口。
        if (window.Klarna && window.Klarna.OnsiteMessaging && typeof window.Klarna.OnsiteMessaging.refresh === 'function') {
          window.Klarna.OnsiteMessaging.refresh();
          return;
        }

        if (window.KlarnaOnsiteService && typeof window.KlarnaOnsiteService.push === 'function') {
          window.KlarnaOnsiteService.push({ eventName: 'refresh-placements' });
        }
      }, 80);
    }

    bindQuantityControls(card) {
      const input = card.querySelector('[data-quantity-input]');
      const minus = card.querySelector('[data-quantity-minus]');
      const plus = card.querySelector('[data-quantity-plus]');
      const select = card.querySelector('[data-variant-select]');
      const display = card.querySelector('[data-quantity-display]');

      if (!input) return;

      const syncPrice = () => {
        input.value = this.normalizeQuantity(input.value);
        if (display) display.textContent = input.value;
        if (select) {
          select.dispatchEvent(new CustomEvent('change', {
            detail: {
              skipVariantImage: true
            }
          }));
        }
      };

      if (minus) {
        minus.addEventListener('click', () => {
          input.value = Math.max(1, this.normalizeQuantity(input.value) - 1);
          syncPrice();
        });
      }

      if (plus) {
        plus.addEventListener('click', () => {
          input.value = this.normalizeQuantity(input.value) + 1;
          syncPrice();
        });
      }

      input.addEventListener('change', syncPrice);
    }

    bindAddToCart(card) {
      const button = card.querySelector('[data-add-to-cart]');
      const select = card.querySelector('[data-variant-select]');
      const input = card.querySelector('[data-quantity-input]');

      if (!button || !select || !input) return;

      button.addEventListener('click', async () => {
        if (button.disabled) return;

        const variantId = select.value;
        const selectedOption = select.options[select.selectedIndex];
        const planVariantId = selectedOption ? selectedOption.dataset.planVariantId : '';
        const quantity = Math.max(1, parseInt(input.value, 10) || 1);
        const originalLabel = button.textContent;

        if (!variantId) return;

        button.disabled = true;
        button.textContent = button.dataset.loadingLabel || 'Adding...';

        try {
          const item = {
            id: variantId,
            quantity: quantity
          };

          const properties = {};
          if (planVariantId) {
            properties._plan_variant_id = String(planVariantId);
          }

          if (Object.keys(properties).length > 0) {
            item.properties = properties;
          }

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              items: [item]
            })
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.description || 'Failed to add to cart');
          }

          document.dispatchEvent(new CustomEvent('gtm:product-added', {
            detail: {
              response: data,
              variantId: variantId,
              quantity: quantity,
              button: button
            }
          }));

          if (window.Alpine && Alpine.store && Alpine.store('xMiniCart')) {
            if (typeof Alpine.store('xMiniCart').reLoad === 'function') {
              Alpine.store('xMiniCart').reLoad();
            }

            window.setTimeout(() => {
              if (typeof Alpine.store('xMiniCart').openCart === 'function') {
                Alpine.store('xMiniCart').openCart();
              }
            }, 300);
          }
        } catch (error) {
          console.error(error);
        } finally {
          button.disabled = false;
          button.textContent = originalLabel;
        }
      });
    }

    formatMoney(cents) {
      const format = this.moneyFormat;
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const match = format.match(placeholderRegex);

      if (!match) {
        return `$${(cents / 100).toFixed(2)}`;
      }

      const token = match[1];
      let value = '';

      switch (token) {
        case 'amount':
          value = this.formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = this.formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = this.formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = this.formatWithDelimiters(cents, 0, '.', ',');
          break;
        default:
          value = this.formatWithDelimiters(cents, 2);
      }

      return format.replace(placeholderRegex, value).replace(/<[^>]*>/g, '');
    }

    formatWithDelimiters(cents, precision = 2, thousands = ',', decimal = '.') {
      if (!Number.isFinite(cents)) return '';

      const number = (cents / 100).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const centsPart = parts[1] ? `${decimal}${parts[1]}` : '';

      return `${dollars}${centsPart}`;
    }
  }

  class SatellaiZoomiesCarousel {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-zoomies-carousel-track]');
      this.prev = root.querySelector('[data-zoomies-carousel-prev]');
      this.next = root.querySelector('[data-zoomies-carousel-next]');
      this.init();
    }

    init() {
      if (!this.track) return;

      if (this.prev) this.prev.addEventListener('click', () => this.scrollByCard(-1));
      if (this.next) this.next.addEventListener('click', () => this.scrollByCard(1));
    }

    scrollByCard(direction) {
      const card = this.track.querySelector(':scope > *');
      const gap = 16;
      const distance = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth;

      this.track.scrollBy({
        left: distance * direction,
        behavior: 'smooth'
      });
    }
  }

  const initZoomiesProducts = (scope = document) => {
    scope.querySelectorAll('[data-zoomies-products-section]').forEach((root) => {
      if (root.dataset.zoomiesInitialized === 'true') return;
      root.dataset.zoomiesInitialized = 'true';
      new SatellaiZoomiesEvent(root);
    });
  };

  const initZoomiesCarousels = (scope = document) => {
    scope.querySelectorAll('[data-zoomies-carousel]').forEach((root) => {
      if (root.dataset.zoomiesCarouselInitialized === 'true') return;
      root.dataset.zoomiesCarouselInitialized = 'true';
      new SatellaiZoomiesCarousel(root);
    });
  };

  const initZoomies = (scope = document) => {
    initZoomiesProducts(scope);
    initZoomiesCarousels(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initZoomies());
  } else {
    initZoomies();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initZoomies(event.target);
  });
}
